import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import DebounceInput from "../DebounceInput/DebounceInput";

const isUppercaseMandatory = true;
const isLowercaseMandatory = true;
const isNumberMandatory = true;
const isSymbolMandatory = true;
const minimumLength = 8;

export const isPasswordCorrect = (password: string | undefined) => {
  if (!password) return false;

  const isUppercase = password.match(/[A-Z]/g);
  const isLowercase = password.match(/[a-z]/g);
  const isNumber = password.match(/[0-9]/g);
  const isSymbol = password.match(/[^\w\s]/g);
  let isError = false;

  if (isUppercaseMandatory && isUppercase === null) isError = true;
  if (isLowercaseMandatory && isLowercase === null) isError = true;
  if (isNumberMandatory && isNumber === null) isError = true;
  if (isSymbolMandatory && isSymbol === null) isError = true;
  if (password.length < minimumLength) isError = true;

  return !isError;
};

const getStrength = (password: string | undefined) => {
  if (!password) return 0;

  const isUppercase = password.match(/[A-Z]/g);
  const isLowercase = password.match(/[a-z]/g);
  const isNumber = password.match(/[0-9]/g);
  const isSymbol = password.match(/[^\w\s]/g);
  let passwordStrength: any = 0;

  if (isUppercase && isLowercase && isNumber && isSymbol) {
    passwordStrength = 6.49;
  } else if (isUppercase && isLowercase && isNumber) {
    passwordStrength = 5.95;
  } else if (isUppercase && isLowercase && isSymbol) {
    passwordStrength = 6.1;
  } else if (isUppercase && isLowercase) {
    passwordStrength = 5.7;
  } else if ((isUppercase || isLowercase) && isNumber) {
    passwordStrength = 5.17;
  } else if (isUppercase || isLowercase) {
    passwordStrength = 4.7;
  } else if (isNumber) {
    passwordStrength = 3.32;
  }

  // If something is missing, keep the score low
  if (!isPasswordCorrect(password)) return 5;

  return Math.round(parseFloat(passwordStrength) * password.length);
};

const getPasswordColor = (strength: number, t: any) => {
  if (strength <= 49) {
    return {
      background: "darkred",
      text: t("components.password.strength.veryWeak"),
      textColor: "white",
    };
  }
  if (strength <= 65) {
    return {
      background: "orange",
      text: t("components.password.strength.weak"),
      textColor: "white",
    };
  }
  if (strength <= 78) {
    return {
      background: "goldenrod",
      text: t("components.password.strength.medium"),
      textColor: "white",
    };
  }
  if (strength <= 82) {
    return {
      background: "goldenrod",
      text: t("components.password.strength.good"),
      textColor: "white",
    };
  }
  if (strength <= 104) {
    return {
      background: "yellowgreen",
      text: t("components.password.strength.strong"),
      textColor: "white",
    };
  }

  return {
    background: "forestgreen",
    text: t("components.password.strength.veryStrong"),
    textColor: "white",
  };
};

type Props = {
  onChange?: (value: any) => any;
  value?: any;
  className?: any;
};

function JPassword({ onChange, value, className, ...props }: Props) {
  const strength = getStrength(value);
  const { t } = useTranslation();

  const [infos, setInfos] = useState({
    background: "darkred",
    text: t("components.password.strength.veryWeak"),
    textColor: "white",
  });

  useEffect(() => {
    const newStrength = getStrength(value);
    setInfos(getPasswordColor(newStrength, t));
  }, [value, t]);

  return (
    <div>
      <DebounceInput
        type="password"
        className={className}
        onChange={onChange}
        value={value}
        {...props}
      />

      {value !== "" && (
        <div
          style={{
            marginTop: "10px",
            backgroundColor: infos?.background,
            width: `${strength > 100 ? 100 : strength}%`,
            padding: "0px 5px",
            borderRadius: 8,
            minWidth: 100,
            fontSize: 12,
            fontWeight: "bold",
          }}
        >
          <span style={{ color: infos?.textColor }}>{infos?.text}</span>
        </div>
      )}
    </div>
  );
}

export default JPassword;
